import { colorGreen500, colorGreenPale, colorYellowMedium } from "../../rsc/colors/colors"

export type DeforestationType = 'MSD' | 'False' | 'Non-MSD' | 'True' | 'Unknown'

export type FruitSite = {
    /** Fruit site id; undefined for the legacy `fruit_origin` position. */
    id?: number
    coordinates: [number, number]
    siteType?: string
}

const isValidCoordinate = (coords: any): boolean =>
    Array.isArray(coords) && coords.length === 2 && coords.every(c => Number.isFinite(Number(c)));

/**
 * Positioned fruit sites of one seller statement: its `fruit_sites`, or the single
 * `fruit_origin` GPS point for statements recorded before sites existed.
 */
export const statementFruitSites = (statement: any): FruitSite[] => {
    const raw = Array.isArray(statement?.fruit_sites) && statement.fruit_sites.length > 0
        ? statement.fruit_sites.map((site: any) => ({
            id: site?.id,
            coordinates: site?.position?.coordinates,
            siteType: site?.siteType,
        }))
        : [{ coordinates: statement?.fruit_origin?.coordinates }];

    return raw
        .filter((site: any) => isValidCoordinate(site.coordinates))
        .map((site: any) => ({ ...site, coordinates: [Number(site.coordinates[0]), Number(site.coordinates[1])] }));
};

export class FruitTraceability {
    contract_id
    market_day
    status
    quantity
    price
    seller_statements
    _created_at
    _updated_at
    mill_id
    mill_name
    /** Whether the contract has been invoiced; undefined until the BI exposes the field. */
    invoiced?: boolean

    constructor(d: any) {
        this.contract_id = d.contract_id
        this.market_day = d.market_day
        this.status = d.status
        this.quantity = d.quantity
        this.seller_statements = d.seller_statements
        this._created_at = d._created_at
        this._updated_at = d._updated_at
        this.mill_id = d.mill_id
        this.mill_name = d.mill_name
        this.price = d.price
        this.invoiced = d.invoiced
    }

    isInvoiced() {
        return this.invoiced === true
    }

    isNac() {
        return this.status === "AUTOCOMPLETED"
    }

    traceabilityLabel(): string | undefined {
        return this.seller_statements?.at(0)?.label?.toLowerCase();
    }

    /** Number of fruit sources declared on the contract; a statement without a positioned site still counts as one. */
    sourceCount(): number {
        return (this.seller_statements ?? []).reduce(
            (sum: number, statement: any) => sum + Math.max(statementFruitSites(statement).length, 1), 0)
    }

    /** Raw deforestation value, for callers that map it to their own styling. */
    deforestationRawLabel(): DeforestationType | undefined {
        return this.seller_statements?.at(0)?.deforestation_label
    }

    deforestationLabel(): string {
        const label = this.seller_statements?.at(0)?.deforestation_label
        switch (label) {
            case 'True':
                return 'deforestation_risk_detected'
            case 'False':
                return 'deforestation_no_risk'
            case 'MSD':
                return 'deforestation_msd_verified'
            case 'Non-MSD':
                return 'deforestation_msd_deforested'
            default:
                return 'deforestation_not_found'
        }
    }

    deforestationLabelColor(): string {
        const label = this.seller_statements?.at(0)?.deforestation_label
        switch (label) {
            case 'MSD':
                return colorGreen500
            case 'Non-MSD':
                return colorYellowMedium()
            case 'True':
                return colorYellowMedium()
            case 'False':
                return colorGreen500
            default:
                return colorGreenPale()
        }
    }

    static build(d): FruitTraceability {
        return new FruitTraceability(d)
    }
}